import type { OfflineDatabaseSqlStatement } from './offlineDatabase.statements';
import type { OfflineResponse } from './offlineDatabase.types';

type ResponseSyncStatus = OfflineResponse['syncStatus'];

export interface ResponseRowWriteValues {
  environmentKey: string;
  tempId: string | null;
  reviewId: number | undefined;
  tempReviewId: string | null;
  itemId: number | undefined;
  roomId: number | null;
  floor: string | null;
  isPresent: number;
  status: OfflineResponse['status'];
  responseData: string | null;
  notes: string | null;
  findings?: string | null;
  syncStatus: ResponseSyncStatus;
  itemSnapshot: string | null;
  lastLocalMutationAt?: string | null;
  lastServerSeenAt?: string | null;
  localRevision?: number;
  serverRevision?: number;
  conflictStatus?: OfflineResponse['conflictStatus'];
  createdAt: string;
  updatedAt: string;
}

export interface CachedResponseRowWriteValues extends ResponseRowWriteValues {
  id: number;
}

export const buildResponseSelectStatement = (
  id: number,
  environmentKey?: string
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT * FROM responses WHERE id = ?${environmentKey ? ' AND environment_key = ?' : ''}`,
  params: environmentKey ? [id, environmentKey] : [id],
});

export const buildResponsesByReviewSelectStatement = (
  reviewId: number,
  environmentKey?: string
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT * FROM responses WHERE review_id = ?${environmentKey ? ' AND environment_key = ?' : ''} ORDER BY created_at ASC`,
  params: environmentKey ? [reviewId, environmentKey] : [reviewId],
});

export const buildResponsesByTempReviewSelectStatement = (
  tempReviewId: string,
  environmentKey?: string
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT * FROM responses WHERE temp_review_id = ?${environmentKey ? ' AND environment_key = ?' : ''} ORDER BY created_at ASC`,
  params: environmentKey ? [tempReviewId, environmentKey] : [tempReviewId],
});

export const buildResponsesBySyncStatusSelectStatement = (
  statuses: ResponseSyncStatus[],
  environmentKey?: string
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT * FROM responses WHERE sync_status IN (${statuses.map(() => '?').join(', ')})${environmentKey ? ' AND environment_key = ?' : ''} ORDER BY created_at ASC`,
  params: environmentKey ? [...statuses, environmentKey] : statuses,
});

export const buildResponseDeleteStatement = (
  id: number
): OfflineDatabaseSqlStatement => ({
  sql: 'DELETE FROM responses WHERE id = ?',
  params: [id],
});

export const buildCachedResponseUpsertStatement = (
  values: CachedResponseRowWriteValues
): OfflineDatabaseSqlStatement => ({
	  sql: `INSERT OR REPLACE INTO responses (
	          id, environment_key, temp_id, review_id, temp_review_id, item_id, room_id, floor,
	          is_present, status, response_data, notes, findings, sync_status,
	          item_snapshot, last_local_mutation_at, last_server_seen_at,
	          local_revision, server_revision, conflict_status, created_at, updated_at
	        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
	  params: [
	    values.id,
	    values.environmentKey,
	    values.tempId,
    values.reviewId,
    values.tempReviewId,
    values.itemId,
    values.roomId,
    values.floor,
    values.isPresent,
    values.status,
    values.responseData,
    values.notes,
    values.findings ?? null,
    values.syncStatus,
    values.itemSnapshot,
    values.lastLocalMutationAt ?? null,
    values.lastServerSeenAt ?? null,
    values.localRevision ?? 0,
    values.serverRevision ?? 0,
    values.conflictStatus ?? 'none',
    values.createdAt,
    values.updatedAt,
  ],
});

export const buildResponseInsertStatement = (
  values: ResponseRowWriteValues
): OfflineDatabaseSqlStatement => ({
	  sql: `INSERT INTO responses (
	          environment_key, temp_id, review_id, temp_review_id, item_id, room_id, floor,
	          is_present, status, response_data, notes, findings, sync_status,
	          item_snapshot, last_local_mutation_at, last_server_seen_at,
	          local_revision, server_revision, conflict_status, created_at, updated_at
	        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
	  params: [
	    values.environmentKey,
	    values.tempId,
    values.reviewId,
    values.tempReviewId,
    values.itemId,
    values.roomId,
    values.floor,
    values.isPresent,
    values.status,
    values.responseData,
    values.notes,
    values.findings ?? null,
    values.syncStatus,
    values.itemSnapshot,
    values.lastLocalMutationAt ?? null,
    values.lastServerSeenAt ?? null,
    values.localRevision ?? 0,
    values.serverRevision ?? 0,
    values.conflictStatus ?? 'none',
    values.createdAt,
    values.updatedAt,
  ],
});
